import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmt, pct, biasColor } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard, CardSkeleton } from "@/components/Bits";
import { SignalCard } from "@/components/SignalCard";
import { DemoBadge } from "@/components/DemoBadge";
import { Disclaimer } from "@/components/Disclaimer";
import { TrendingUp, Wallet, Radar, Gauge, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { money } = useCurrency();
  const [markets, setMarkets] = useState(null);
  const [signals, setSignals] = useState([]);
  const [account, setAccount] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [regime, setRegime] = useState(null);

  useEffect(() => {
    api.get("/markets").then((r) => setMarkets(r.data.assets)).catch(() => setMarkets([]));
    api.get("/signals?limit=3&min_confidence=55").then((r) => setSignals(r.data.signals.slice(0, 3))).catch(() => {});
    api.get("/paper/account").then((r) => setAccount(r.data)).catch(() => {});
    api.get("/signals/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/markets/regime").then((r) => setRegime(r.data)).catch(() => {});
  }, []);

  const bullish = (markets || []).filter((m) => m.ai_bias === "bullish").length;
  const bearish = (markets || []).filter((m) => m.ai_bias === "bearish").length;
  const total = (markets || []).length || 1;
  const marketBias = bullish > bearish ? "Bullish" : bearish > bullish ? "Bearish" : "Neutral";

  return (
    <DashboardLayout title={`Welcome, ${user?.name?.split(" ")[0] || "Trader"}`}>
      <div className="mb-4 flex items-center gap-2">
        <DemoBadge />
        <span className="text-sm text-muted-foreground">All market data is simulated demo data.</span>
      </div>

      {/* Market regime */}
      {regime && (
        <div className="mb-6 rounded-2xl border border-border bg-card p-5" data-testid="market-regime">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">AI Market Regime</div>
              <div className="mt-1 flex items-center gap-2">
                <span className={`font-display text-2xl font-bold ${regime.regime.includes("BULL") ? "text-bullish" : regime.regime.includes("BEAR") ? "text-bearish" : regime.regime.includes("VOLAT") ? "text-warning" : "text-foreground"}`}>{regime.regime}</span>
              </div>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">{regime.reason}</p>
            </div>
            <div className="flex gap-6 text-center">
              <div><div className="font-mono text-lg font-bold text-bullish">{regime.bullish}</div><div className="text-[10px] uppercase text-muted-foreground">Bullish</div></div>
              <div><div className="font-mono text-lg font-bold text-bearish">{regime.bearish}</div><div className="text-[10px] uppercase text-muted-foreground">Bearish</div></div>
              <div><div className="font-mono text-lg font-bold">{regime.avg_volatility}%</div><div className="text-[10px] uppercase text-muted-foreground">Avg Vol</div></div>
            </div>
          </div>
        </div>
      )}

      {/* Top stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="AI Market Bias" value={marketBias} accent={biasColor(marketBias.toLowerCase())} icon={Gauge} testid="stat-market-bias" sub={`${bullish} bullish · ${bearish} bearish`} />
        <StatCard label="Paper Equity" value={account ? money(account.equity) : "—"} icon={Wallet} testid="stat-equity" sub={account ? `${account.total_pnl >= 0 ? "+" : ""}${money(account.total_pnl)} total P&L` : ""} />
        <StatCard label="Signal Win Rate" value={analytics ? `${analytics.win_rate}%` : "—"} icon={Radar} testid="stat-winrate" sub={analytics ? `${analytics.wins}W / ${analytics.losses}L` : ""} />
        <StatCard label="Avg Risk/Reward" value={analytics ? analytics.avg_rr : "—"} icon={TrendingUp} testid="stat-avgrr" sub="across published signals" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Market overview */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">Market Overview</h2>
            <Link to="/app/markets" className="text-xs text-primary">View all →</Link>
          </div>
          {!markets ? <CardSkeleton count={2} /> : (
            <div className="grid gap-2 sm:grid-cols-2">
              {markets.slice(0, 8).map((m) => (
                <Link to={`/app/markets`} key={m.symbol} className="flex items-center justify-between rounded-xl border border-border px-3 py-2.5 hover:bg-secondary/50">
                  <div><div className="font-display text-sm font-semibold">{m.symbol}</div><div className={`text-[10px] font-semibold uppercase ${biasColor(m.ai_bias)}`}>{m.ai_bias}</div></div>
                  <div className="text-right"><div className="font-mono text-sm">{fmt(m.price)}</div><div className={`text-xs ${m.change_pct >= 0 ? "text-bullish" : "text-bearish"}`}>{pct(m.change_pct)}</div></div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Risk monitor + paper */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">Paper Portfolio</h2>
            {account ? (
              <div className="mt-3 space-y-2 text-sm">
                <Row label="Balance" value={money(account.balance)} />
                <Row label="Equity" value={money(account.equity)} />
                <Row label="Open positions" value={account.open_positions.length} />
                <Row label="Total P&L" value={money(account.total_pnl)} color={account.total_pnl >= 0 ? "text-bullish" : "text-bearish"} />
                <Link to="/app/paper-trading" className="mt-2 flex items-center gap-1 text-xs text-primary">Open paper trading <ArrowRight className="h-3 w-3" /></Link>
              </div>
            ) : <p className="mt-3 text-sm text-muted-foreground">Loading…</p>}
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">Risk Monitor</h2>
            <p className="mt-2 text-sm text-muted-foreground">Risk preference: <span className="font-semibold capitalize text-foreground">{user?.risk_preference || "moderate"}</span></p>
            <Link to="/app/risk" className="mt-3 flex items-center gap-1 text-xs text-primary">Open risk calculator <ArrowRight className="h-3 w-3" /></Link>
          </div>
        </div>
      </div>

      {/* Top signals */}
      <div className="mt-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold">Top AI Signals</h2>
          <Link to="/app/signals" className="text-xs text-primary">View all signals →</Link>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {signals.map((s) => <SignalCard key={s.asset + s.timeframe} s={s} />)}
        </div>
      </div>

      <div className="mt-6"><Disclaimer /></div>
    </DashboardLayout>
  );
}

function Row({ label, value, color }) {
  return <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className={`font-mono font-medium ${color || ""}`}>{value}</span></div>;
}
